import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { isNavItemLocked } from "../../../api/scaNav";
import { APP_VERSION } from "../../../constants/appBranding";
import { useNav } from "../../../context/NavContext";
import { useContractPath } from "../../../hooks/useContractPath";
import { appPathname } from "../../../utils/contractPath";
import { navRouteMatches } from "../../../utils/navRouteMatch";
import "./AppSidebar.css";

const OPEN_SECTIONS_KEY = "spm.appSidebar.openSections";

function readPersistedOpenSections() {
  try {
    const raw = sessionStorage.getItem(OPEN_SECTIONS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.map((x) => String(x)).filter(Boolean));
  } catch {
    return new Set();
  }
}

function persistOpenSections(set) {
  try {
    sessionStorage.setItem(
      OPEN_SECTIONS_KEY,
      JSON.stringify(Array.from(set))
    );
  } catch {
    /* ignore */
  }
}

/**
 * Painel lateral: secções e itens vindos de /sca/nav/menu (via NavContext).
 * Itens sem acesso (auth / licença / empresa) vêm omitidos da API — não se
 * mostram desabilitados.
 * O aside/coluna e o botão recolher ficam no AppShell.
 */
export function AppSidebar({ collapsed = false, onNavigate }) {
  const location = useLocation();
  const cp = useContractPath();
  const {
    sections,
    loading,
    error,
    toggleFavorite,
    favBusy,
  } = useNav();
  const [openSections, setOpenSections] = useState(readPersistedOpenSections);

  const visibleSections = useMemo(
    () =>
      (sections || [])
        .map((sec) => ({
          ...sec,
          items: (sec.items || []).filter((it) => !isNavItemLocked(it)),
        }))
        .filter((sec) => (sec.items || []).length > 0),
    [sections]
  );

  // Garante que a secção da rota actual fica aberta (AppShell remonta por rota).
  // Não substitui o conjunto — só acrescenta matches; o utilizador pode manter
  // outras secções abertas. Persistência cobre o remount do sidebar.
  useEffect(() => {
    setOpenSections((prev) => {
      const path = appPathname(location.pathname);
      const matched = new Set();
      for (const sec of visibleSections) {
        const hit = (sec.items || []).some((it) =>
          navRouteMatches(it.ds_route, path, location.search)
        );
        if (hit) matched.add(sec.cd_section);
      }

      // Ainda a carregar o catálogo: manter estado persistido / actual.
      if (visibleSections.length === 0) {
        return prev;
      }

      const next = new Set(prev);
      for (const cd of matched) next.add(cd);
      if (matched.size === 0 && next.size === 0 && visibleSections[0]) {
        next.add(visibleSections[0].cd_section);
      }

      const same =
        next.size === prev.size && [...next].every((cd) => prev.has(cd));
      if (same) return prev;
      persistOpenSections(next);
      return next;
    });
  }, [visibleSections, location.pathname, location.search]);

  const toggleSection = useCallback((cd) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(cd)) next.delete(cd);
      else next.add(cd);
      persistOpenSections(next);
      return next;
    });
  }, []);

  const onFavClick = async (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await toggleFavorite(item);
    } catch {
      /* erro já no contexto */
    }
  };

  if (collapsed) {
    return null;
  }

  return (
    <nav className="app-sidebar" aria-label="Menu de navegação">
      <div className="app-sidebar__inner">
        {loading && visibleSections.length === 0 ? (
          <p className="app-sidebar__status">A carregar…</p>
        ) : error && visibleSections.length === 0 ? (
          <p className="app-sidebar__status app-sidebar__status--error" role="alert">
            {error}
          </p>
        ) : visibleSections.length === 0 ? (
          <p className="app-sidebar__status">Nenhum menu disponível.</p>
        ) : (
          visibleSections.map((sec) => {
            const open = openSections.has(sec.cd_section);
            return (
              <div key={sec.cd_section} className="app-sidebar__section">
                <button
                  type="button"
                  className="app-sidebar__section-btn"
                  aria-expanded={open}
                  onClick={() => toggleSection(sec.cd_section)}
                >
                  <span>{sec.nm_section}</span>
                  <span className="app-sidebar__chevron" aria-hidden>
                    {open ? "▾" : "▸"}
                  </span>
                </button>
                {open ? (
                  <ul className="app-sidebar__list">
                    {(sec.items || []).map((item) => {
                      const appPath = appPathname(location.pathname);
                      const active = navRouteMatches(
                        item.ds_route,
                        appPath,
                        location.search
                      );
                      return (
                        <li key={item.cd_menu} className="app-sidebar__row">
                          <Link
                            className={
                              active
                                ? "app-sidebar__link app-sidebar__link--active"
                                : "app-sidebar__link"
                            }
                            to={cp(item.ds_route)}
                            onClick={onNavigate}
                          >
                            {item.nm_menu}
                          </Link>
                          {item.fl_allow_favorite ? (
                            <button
                              type="button"
                              className={
                                item.fl_favorite
                                  ? "app-sidebar__fav app-sidebar__fav--on"
                                  : "app-sidebar__fav"
                              }
                              title={
                                item.fl_favorite
                                  ? "Remover dos favoritos"
                                  : "Adicionar aos favoritos"
                              }
                              aria-label={
                                item.fl_favorite
                                  ? `Remover ${item.nm_menu} dos favoritos`
                                  : `Favoritar ${item.nm_menu}`
                              }
                              disabled={favBusy === item.cd_menu}
                              onClick={(e) => void onFavClick(item, e)}
                            >
                              {item.fl_favorite ? "★" : "☆"}
                            </button>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </div>
            );
          })
        )}
      </div>
      <p className="app-sidebar__version">Versão {APP_VERSION}</p>
    </nav>
  );
}
